import { validateDate, validateMobileInSearch, validateIntegerInSearch } from '@/utils/validators'
import { finnishDateStringToIsoDateString } from '@/utils/date-mapper'
import { removeSpecialCharacterPhoneNumbers } from '@/utils/string-manipulation'
import { searchInput } from '@/utils/format-search-string'
import { TWILIO_SENT_STATUSES, TWILIO_FAILED_STATUSES } from '@/utils/twilio-status'

/**
 * the SEARCH_NOTIFICATION_AUTOCOMPLETE_INPUTS array contains all the search inputs that will be used as suggestions in the search bar
 */
function createSearchNotificationAutocompleteInputs () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return [
    messages.getTableColumnPlant(),
    messages.getTableColumnStorageLocation(),
    messages.getTableColumnEquipmentCode(),
    messages.getTableColumnEquipmentDescription(),
    messages.getTableColumnWorkCenter(),
    messages.getTableColumnPlannerGroup(),
    messages.getTableColumnTrackingNumber(),
    messages.getTableColumnPurchaseOrder(),
    messages.getTableColumnSalesOrder(),
    messages.getTableColumnDeliveryOrder(),
    messages.getTableColumnServiceOrder(),
    messages.getTableColumnMaterialCode(),
    messages.getTableColumnMaterialDescription(),
    messages.getTableColumnDeliveryDate(),
    messages.getTableColumnCreated(),
    messages.getTableColumnEmail()
  ]
}

/**
 * COMMON_NOTIFICATION_SEARCH_INPUTS contains all the search inputs
 * the key is the name of the input provided to the backend search api
 * the value is an object that contains the following:
 * 1- validator: a function that takes a string and returns a boolean indicating whether the string is valid or not
 * 2- mapper: a function that takes a string and returns another string
 * 3- items: an array of strings that will be used to match the input with the key
 */
function createCommonNotificationSearchInputs () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return {
    purchase_order: searchInput(['purchase order', 'purchaseorder', 'po', messages.getTableColumnPurchaseOrder()]),
    delivery_order: searchInput(['delivery order', 'deliveryorder', 'do', messages.getTableColumnDeliveryOrder()]),
    sales_order: searchInput(['sales order', 'salesorder', messages.getTableColumnSalesOrder()]),
    service_order: searchInput(['service order', 'serviceorder', 'so', messages.getTableColumnServiceOrder()]),
    storage_location: searchInput(['sloc', 'storage location', 'storagelocation', messages.getTableColumnStorageLocation()]),
    tracking_number: searchInput(['tracking number', 'trackingnumber', 'tn', 'tracking_number', messages.getTableColumnTrackingNumber()]),
    plant: searchInput(['plant', 'plants', messages.getTableColumnPlant()]),
    planner_group: searchInput(['planner group', 'plannergroup', messages.getTableColumnPlannerGroup()]),
    equipment_code: searchInput(['equipment', 'equipment code', 'equipmentcode', 'ec', messages.getTableColumnEquipmentCode()]),
    equipment_description: searchInput(['equipment description', 'equipmentdescription', 'eqd', messages.getTableColumnEquipmentDescription()]),
    work_center: searchInput(['work center', 'workcenter', 'wc', messages.getTableColumnWorkCenter()]),
    user_email: searchInput(['user email', 'ue', 'email', messages.getTableColumnEmail()]),
    material_code: searchInput(['material', 'mat', 'material code', 'materialcode', 'mc', messages.getTableColumnMaterialCode()]),
    material_description: searchInput(['material description', 'materialdescription', 'matd', messages.getTableColumnMaterialDescription()]),
    delivery_date: searchInput(['delivery date', 'dd', messages.getTableColumnDeliveryDate()], validateDate, finnishDateStringToIsoDateString),
    // KPT-1118: same alias list as Stock Count's Created filter. Keeping the
    // aliases identical across every notification input set is what lets
    // filterApplicableSearchTags carry a Created tag over tab switches.
    created: searchInput(['created', 'creation date', 'creationdate', messages.getTableColumnCreated()], validateDate, finnishDateStringToIsoDateString)
  }
}

/**
 * KPT-1117: canFilterTwilio adds the Twilio delivery filters (SMS Status,
 * Error Code). They are restricted to super_manager / super_manager_read_only
 * because the API omits the underlying columns for every other role - offering
 * a filter whose results cannot be seen (and which the backend rejects) would
 * only produce errors. The store getter owns that decision so the inputs and
 * the suggestion list can never disagree.
 */
export function createNotificationSmsSearchInputs (canFilterTwilio = false) {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  const commonInputs = createCommonNotificationSearchInputs()
  const inputs = {
    ...commonInputs,
    phone_number: searchInput(['phone number', 'phonenumber', 'pn', messages.getTableColumnPhoneNumber()], validateMobileInSearch, removeSpecialCharacterPhoneNumbers)
  }
  if (canFilterTwilio) {
    inputs.twilio_status = searchInput(['sms status', 'smsstatus', 'twilio status', 'twilio_status', messages.getTableColumnSmsStatus()], validateDeliveryStatusSearchValue, mapDeliveryStatusSearchValue)
    inputs.twilio_error_code = searchInput(['error code', 'errorcode', 'error_code', 'twilio error code', messages.getTableColumnErrorCode()], validateIntegerInSearch)
  }
  return inputs
}

/**
 * KPT-1117 (item 1): the param set of the merged "All" stream
 * (/sms_notifications/all_logs), which UNIONs sms_notification and uap_sms_log.
 * KPT-1121: widened to the full union of the sub-tabs' criteria - the backend
 * accepts each one-sided param and excludes the branch whose table lacks the
 * column (country hides sms rows; the order/material/delivery-date diagnostics
 * hide uap_log rows).
 *
 * Picked out of the privileged SMS set rather than redefined, so the aliases,
 * validators and mappers cannot drift: alias parity is exactly what lets
 * filterApplicableSearchTags carry a tag from the delivery tab to All to the
 * UAP tab and back. country is the one key the SMS set lacks - it is added in
 * the factory with the UAP set's alias list, for the same carry-over reason.
 *
 * Must never contain if_sms_success or the UAP spellings (error_code,
 * fitter_id, tracking_num): getKeysByValue resolves a tag label to EVERY
 * matching key, so a set holding both spellings of one concept would send both
 * params. notification_type stays out too - the All radio carries
 * ignore_filter, the stream is every type.
 */
const SMS_ALL_LOGS_INPUT_KEYS = [
  'plant',
  'work_center',
  'planner_group',
  'storage_location',
  'user_email',
  'tracking_number',
  'phone_number',
  'twilio_status',
  'twilio_error_code',
  'created',
  // KPT-1121: the sms_notification-only diagnostics the delivery tabs offer.
  'purchase_order',
  'delivery_order',
  'sales_order',
  'service_order',
  'equipment_code',
  'equipment_description',
  'material_code',
  'material_description',
  'delivery_date'
]

export function createNotificationSmsAllLogsSearchInputs () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  const smsInputs = createNotificationSmsSearchInputs(true)
  const inputs = SMS_ALL_LOGS_INPUT_KEYS.reduce((picked, key) => {
    picked[key] = smsInputs[key]
    return picked
  }, {})
  // KPT-1121: identical alias list to the UAP set's country input - the parity
  // is what keeps a Country tag carrying across the UAP <-> All switch.
  inputs.country = searchInput(['country', messages.getTableColumnCountry()])
  return inputs
}

export function createNotificationSmsAllLogsSuggestions () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return [
    messages.getTableColumnPlant(),
    messages.getTableColumnWorkCenter(),
    messages.getTableColumnPlannerGroup(),
    messages.getTableColumnStorageLocation(),
    messages.getTableColumnEmail(),
    messages.getTableColumnTrackingNumber(),
    messages.getTableColumnPhoneNumber(),
    messages.getTableColumnSmsStatus(),
    messages.getTableColumnErrorCode(),
    messages.getTableColumnCreated(),
    // KPT-1121: the widened criteria.
    messages.getTableColumnCountry(),
    messages.getTableColumnPurchaseOrder(),
    messages.getTableColumnDeliveryOrder(),
    messages.getTableColumnSalesOrder(),
    messages.getTableColumnServiceOrder(),
    messages.getTableColumnEquipmentCode(),
    messages.getTableColumnEquipmentDescription(),
    messages.getTableColumnMaterialCode(),
    messages.getTableColumnMaterialDescription(),
    messages.getTableColumnDeliveryDate()
  ].sort()
}

export function createNotificationSmsSuggestions (canFilterTwilio = false) {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  const autocompleteInputs = createSearchNotificationAutocompleteInputs()
  const suggestions = [
    ...autocompleteInputs,
    messages.getTableColumnPhoneNumber()
  ]
  if (canFilterTwilio) {
    suggestions.push(messages.getTableColumnSmsStatus(), messages.getTableColumnErrorCode())
  }
  return suggestions.sort()
}

/**
 * KPT-1117: map the SMS Status filter value to the twilio_status values the
 * backend stores. Unlike the UAP tab's boolean if_sms_success, twilio_status is
 * a seven-value lifecycle column, so one dropdown choice expands to a
 * comma-separated list which the backend splits and ORs.
 *
 * The bucket aliases are matched BEFORE the raw statuses on purpose: 'sent' is
 * both the Sent bucket's English alias and a Twilio status of its own, and the
 * bucket has to win - otherwise picking Sent would quietly return only the
 * literally-'sent' rows and hide the delivered and queued ones.
 */
function mapDeliveryStatusSearchValue (value) {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  const normalized = String(value).trim().toLowerCase()
  const bucketMap = {
    [TWILIO_SENT_STATUSES.join(',')]: ['sent', messages.getLabelSent().toLowerCase()],
    [TWILIO_FAILED_STATUSES.join(',')]: ['failed', messages.getLabelFailed().toLowerCase()]
  }
  for (const [statuses, aliases] of Object.entries(bucketMap)) {
    if (aliases.includes(normalized)) {
      return statuses
    }
  }
  // A raw Twilio status typed by hand is passed through untouched.
  return normalized
}

function validateDeliveryStatusSearchValue (value) {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  if (!value) return false
  const validValues = [
    'sent',
    'failed',
    messages.getLabelSent().toLowerCase(),
    messages.getLabelFailed().toLowerCase(),
    ...TWILIO_SENT_STATUSES,
    ...TWILIO_FAILED_STATUSES
  ]
  return validValues.includes(String(value).trim().toLowerCase())
}

/**
 * KPT-1035: UAP logs search inputs. The keys are the column-filter params of
 * /user_api/v1/sms_notifications/uap_logs - deliberately NOT the common set,
 * which contains params that endpoint rejects (purchase_order, user_email,
 * delivery_date, ...) and uses tracking_number instead of tracking_num.
 */
function createNotificationUapLogsSearchInputs () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return {
    plant: searchInput(['plant', 'plants', messages.getTableColumnPlant()]),
    work_center: searchInput(['work center', 'workcenter', 'wc', messages.getTableColumnWorkCenter()]),
    planner_group: searchInput(['planner group', 'plannergroup', messages.getTableColumnPlannerGroup()]),
    storage_location: searchInput(['sloc', 'storage location', 'storagelocation', messages.getTableColumnStorageLocation()]),
    equipment_code: searchInput(['equipment', 'equipment code', 'equipmentcode', 'ec', messages.getTableColumnEquipmentCode()]),
    service_order: searchInput(['service order', 'serviceorder', 'so', messages.getTableColumnServiceOrder()]),
    tracking_num: searchInput(['tracking number', 'trackingnumber', 'tn', 'tracking_number', messages.getTableColumnTrackingNumber()]),
    // fitter_id holds the recipient phone number - surfaced to users as
    // Phone Number (the Fitter ID aliases stay for muscle memory). KPT-1116:
    // strip non-digits like the delivery tab's phone_number input, so the
    // backend's partial match hits the +-prefixed stored values.
    fitter_id: searchInput(['phone number', 'phonenumber', 'pn', 'fitter id', 'fitterid', 'fitter_id', 'fitter', messages.getTableColumnPhoneNumber(), messages.getTableColumnFitterId()], validateMobileInSearch, removeSpecialCharacterPhoneNumbers),
    country: searchInput(['country', messages.getTableColumnCountry()]),
    // KPT-1118: identical alias list to the common set's created input - the
    // UAP set is deliberately independent, so the parity is what keeps the
    // Created tag carrying across tab switches.
    created: searchInput(['created', 'creation date', 'creationdate', messages.getTableColumnCreated()], validateDate, finnishDateStringToIsoDateString),
    // Typed backend filters: reject invalid values inline (red tag) instead
    // of letting the backend 400.
    error_code: searchInput(['error code', 'errorcode', 'error_code', messages.getTableColumnErrorCode()], validateIntegerInSearch),
    // KPT-1116: surfaced as SMS Status with SENT/FAILED options (the SMS
    // Success aliases stay for muscle memory); backend still takes booleans.
    if_sms_success: searchInput(['sms status', 'smsstatus', 'sms success', 'smssuccess', 'success', 'if_sms_success', messages.getTableColumnSmsStatus(), messages.getTableColumnSmsSuccess()], validateSmsStatusSearchValue, mapSmsStatusSearchValue)
  }
}

/**
 * KPT-1116: map the SMS Status filter value (localized Sent/Failed dropdown
 * option, English sent/failed, or legacy true/false) to the 'true'/'false'
 * literal the backend's if_sms_success boolean filter accepts.
 */
function mapSmsStatusSearchValue (value) {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  const normalized = String(value).trim().toLowerCase()
  const statusMap = {
    true: ['true', 'sent', messages.getLabelSent().toLowerCase()],
    false: ['false', 'failed', messages.getLabelFailed().toLowerCase()]
  }
  for (const [key, values] of Object.entries(statusMap)) {
    if (values.includes(normalized)) {
      return key
    }
  }
  return normalized
}

function validateSmsStatusSearchValue (value) {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  if (!value) return false
  const validValues = ['true', 'false', 'sent', 'failed', messages.getLabelSent().toLowerCase(), messages.getLabelFailed().toLowerCase()]
  return validValues.includes(String(value).trim().toLowerCase())
}

function createNotificationUapLogsSuggestions () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return [
    messages.getTableColumnPlant(),
    messages.getTableColumnWorkCenter(),
    messages.getTableColumnPlannerGroup(),
    messages.getTableColumnStorageLocation(),
    messages.getTableColumnEquipmentCode(),
    messages.getTableColumnServiceOrder(),
    messages.getTableColumnTrackingNumber(),
    messages.getTableColumnPhoneNumber(),
    messages.getTableColumnCountry(),
    messages.getTableColumnErrorCode(),
    messages.getTableColumnSmsStatus(),
    messages.getTableColumnCreated()
  ].sort()
}

let _notificationEmailAllowedSearchInputs = null
export const NOTIFICATION_EMAIL_ALLOWED_SEARCH_INPUTS = new Proxy({}, {
  get (target, prop) {
    if (!_notificationEmailAllowedSearchInputs) {
      _notificationEmailAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return _notificationEmailAllowedSearchInputs[prop]
  },
  ownKeys (target) {
    if (!_notificationEmailAllowedSearchInputs) {
      _notificationEmailAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return Reflect.ownKeys(_notificationEmailAllowedSearchInputs)
  },
  has (target, prop) {
    if (!_notificationEmailAllowedSearchInputs) {
      _notificationEmailAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return prop in _notificationEmailAllowedSearchInputs
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_notificationEmailAllowedSearchInputs) {
      _notificationEmailAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return Reflect.getOwnPropertyDescriptor(_notificationEmailAllowedSearchInputs, prop)
  }
})

let _notificationsEmailSuggestions = null
export const NOTIFICATIONS_EMAIL_SUGGESTIONS = new Proxy([], {
  get (target, prop) {
    if (!_notificationsEmailSuggestions) {
      _notificationsEmailSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return _notificationsEmailSuggestions[prop]
  },
  ownKeys (target) {
    if (!_notificationsEmailSuggestions) {
      _notificationsEmailSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return Reflect.ownKeys(_notificationsEmailSuggestions)
  },
  has (target, prop) {
    if (!_notificationsEmailSuggestions) {
      _notificationsEmailSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return prop in _notificationsEmailSuggestions
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_notificationsEmailSuggestions) {
      _notificationsEmailSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return Reflect.getOwnPropertyDescriptor(_notificationsEmailSuggestions, prop)
  }
})

// KPT-1117: the SMS set has no lazy Proxy singleton - the store getter calls
// createNotificationSmsSearchInputs() / createNotificationSmsSuggestions()
// directly, because the set now depends on the user's permissions. The Proxies
// only existed to defer the messages.* lookups past module init, which a getter
// body already does.

let _notificationUapLogsAllowedSearchInputs = null
export const NOTIFICATION_UAP_LOGS_ALLOWED_SEARCH_INPUTS = new Proxy({}, {
  get (target, prop) {
    if (!_notificationUapLogsAllowedSearchInputs) {
      _notificationUapLogsAllowedSearchInputs = createNotificationUapLogsSearchInputs()
    }
    return _notificationUapLogsAllowedSearchInputs[prop]
  },
  ownKeys (target) {
    if (!_notificationUapLogsAllowedSearchInputs) {
      _notificationUapLogsAllowedSearchInputs = createNotificationUapLogsSearchInputs()
    }
    return Reflect.ownKeys(_notificationUapLogsAllowedSearchInputs)
  },
  has (target, prop) {
    if (!_notificationUapLogsAllowedSearchInputs) {
      _notificationUapLogsAllowedSearchInputs = createNotificationUapLogsSearchInputs()
    }
    return prop in _notificationUapLogsAllowedSearchInputs
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_notificationUapLogsAllowedSearchInputs) {
      _notificationUapLogsAllowedSearchInputs = createNotificationUapLogsSearchInputs()
    }
    return Reflect.getOwnPropertyDescriptor(_notificationUapLogsAllowedSearchInputs, prop)
  }
})

let _notificationsUapLogsSuggestions = null
export const NOTIFICATIONS_UAP_LOGS_SUGGESTIONS = new Proxy([], {
  get (target, prop) {
    if (!_notificationsUapLogsSuggestions) {
      _notificationsUapLogsSuggestions = createNotificationUapLogsSuggestions()
    }
    return _notificationsUapLogsSuggestions[prop]
  },
  ownKeys (target) {
    if (!_notificationsUapLogsSuggestions) {
      _notificationsUapLogsSuggestions = createNotificationUapLogsSuggestions()
    }
    return Reflect.ownKeys(_notificationsUapLogsSuggestions)
  },
  has (target, prop) {
    if (!_notificationsUapLogsSuggestions) {
      _notificationsUapLogsSuggestions = createNotificationUapLogsSuggestions()
    }
    return prop in _notificationsUapLogsSuggestions
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_notificationsUapLogsSuggestions) {
      _notificationsUapLogsSuggestions = createNotificationUapLogsSuggestions()
    }
    return Reflect.getOwnPropertyDescriptor(_notificationsUapLogsSuggestions, prop)
  }
})

let _notificationPushNotificationAllowedSearchInputs = null
export const NOTIFICATION_PUSH_NOTIFICATION_ALLOWED_SEARCH_INPUTS = new Proxy({}, {
  get (target, prop) {
    if (!_notificationPushNotificationAllowedSearchInputs) {
      _notificationPushNotificationAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return _notificationPushNotificationAllowedSearchInputs[prop]
  },
  ownKeys (target) {
    if (!_notificationPushNotificationAllowedSearchInputs) {
      _notificationPushNotificationAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return Reflect.ownKeys(_notificationPushNotificationAllowedSearchInputs)
  },
  has (target, prop) {
    if (!_notificationPushNotificationAllowedSearchInputs) {
      _notificationPushNotificationAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return prop in _notificationPushNotificationAllowedSearchInputs
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_notificationPushNotificationAllowedSearchInputs) {
      _notificationPushNotificationAllowedSearchInputs = createCommonNotificationSearchInputs()
    }
    return Reflect.getOwnPropertyDescriptor(_notificationPushNotificationAllowedSearchInputs, prop)
  }
})

let _notificationsPushNotificationSuggestions = null
export const NOTIFICATIONS_PUSH_NOTIFICATION_SUGGESTIONS = new Proxy([], {
  get (target, prop) {
    if (!_notificationsPushNotificationSuggestions) {
      _notificationsPushNotificationSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return _notificationsPushNotificationSuggestions[prop]
  },
  ownKeys (target) {
    if (!_notificationsPushNotificationSuggestions) {
      _notificationsPushNotificationSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return Reflect.ownKeys(_notificationsPushNotificationSuggestions)
  },
  has (target, prop) {
    if (!_notificationsPushNotificationSuggestions) {
      _notificationsPushNotificationSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return prop in _notificationsPushNotificationSuggestions
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_notificationsPushNotificationSuggestions) {
      _notificationsPushNotificationSuggestions = createSearchNotificationAutocompleteInputs().sort()
    }
    return Reflect.getOwnPropertyDescriptor(_notificationsPushNotificationSuggestions, prop)
  }
})
